import {
  CalendarEvents,
  DateString,
  OpenShiftsSummaryData,
  OpenShiftsResponse as Response,
  Schedule,
  convertOpenShiftsSummary,
} from '@nextforce/api'
import { useQuery } from '@tanstack/react-query'
import { openShiftsScheduleQueryParams } from 'app/concept_data_handler'
import { t } from 'app/i18l'
import { useServer } from 'app/server/context'
import { BaseHomeItem, NextShiftsTilePageProp, SummaryItem } from 'home/types'
import {
  makeErrorHomeItem,
  makeSuccessHomeItem,
} from 'home/update_app/shared/utils'
import { cloneDeep } from 'lodash'
import moment from 'moment'
import {
  areDefaultFilters,
  defaultIncentivesEnum,
  getDefaultFilters,
  useFiltersContext,
} from 'schedule/filters/context'
import { useSchedule } from 'schedule/queries/list'
import { filterEvents } from 'schedule/schedules'
import { MINS_TO_MS } from 'shared/constants'
import { generateUniqueId } from 'shared/utils'
import { useUser } from 'users/queries/detail'
import keys from './shared/keys'

export function useOpenShiftsSummary<T = OpenShiftsSummaryData>(
  enabled = true,
  select?: (data: OpenShiftsSummaryData) => T
) {
  const { server } = useServer()

  return useQuery<OpenShiftsSummaryData, unknown, T>({
    queryKey: keys.summary(),
    queryFn: fetchOpenShiftsSummary,
    enabled,
    refetchInterval: 15 * MINS_TO_MS,
    retry: false,
    select,
  })

  async function fetchOpenShiftsSummary() {
    return convertOpenShiftsSummary(
      await server.get<Response.OpenShiftsSummary>(
        '/v2/helpers/me/schedule/changes/summary'
      )
    )
  }
}

export function useOpenShiftsHomeItem() {
  const { savedPresets, activeFilterNames } = useFiltersContext()
  const { data: user } = useUser()
  const enabled = user?.permissions?.canSeeNextShifts
  const {
    data: openShiftsScheduleData,
    isError: isOpenShiftsScheduleError,
    error: openShiftsScheduleError,
  } = useSchedule(openShiftsScheduleQueryParams, generateUniqueId, enabled)

  if (!enabled) {
    return
  }

  const baseItem = {
    title: t('myOpenShifts'),
    route: { name: 'open_shifts' },
    color: 'white',
    backgroundColor: '#F73961',
  } as BaseHomeItem

  if (isOpenShiftsScheduleError) {
    return makeErrorHomeItem(baseItem, openShiftsScheduleError)
  }

  const schedule: Schedule = {
    calendar: 'helper',
    custom: true,
    openShiftsOnly: true,
  }
  const tiles: Array<NextShiftsTilePageProp | undefined> = []
  const shifts = openShiftsScheduleData?.events
  if (savedPresets) {
    tiles.push(
      ...savedPresets.flatMap((preset, index) => {
        if (areDefaultFilters(preset.filters)) {
          return []
        }
        const filteredEvents = filterEvents(shifts, schedule, preset.filters)
        if (filteredEvents) {
          const filteredShifts = getOpenShiftsFromCalendarEvents(filteredEvents)
          return {
            name: `${t('nextShiftsMatchingPreset')} ${index + 1}`,
            description: `${t('seeNextShiftsMatching', {
              count: filteredShifts.length,
            })} ${activeFilterNames(preset.filters)}`,
            shifts: filteredShifts,
            params: { filters: preset.filters, schedule },
          }
        }
        return []
      })
    )
  }
  if (tiles.length === 0) {
    tiles.push(createShiftWithIncentivesTile())
  }
  if (tiles.length <= 1) {
    tiles.push(createUpcomingShiftsTile(1))
  }
  if (tiles.length <= 2) {
    tiles.push(createUpcomingShiftsTile(2))
  }

  const summaryItems: SummaryItem[] = []
  for (const item of tiles) {
    if (item) {
      summaryItems.push({
        title: item.name,
        description: item.description,
        params: item.params,
      })
    }
  }

  return makeSuccessHomeItem(baseItem, summaryItems)

  function createShiftWithIncentivesTile() {
    const defaultFilters = getDefaultFilters()
    const shiftsWithIncentivesFilters = cloneDeep(defaultFilters)
    shiftsWithIncentivesFilters.incentives.selectedId =
      defaultIncentivesEnum.nextShiftsWithIncentives.toString()
    const filteredEvents = filterEvents(
      shifts,
      schedule,
      shiftsWithIncentivesFilters
    )
    if (filteredEvents) {
      const filteredShifts = getOpenShiftsFromCalendarEvents(filteredEvents)
      if (filteredShifts.length) {
        return {
          name: t('nextShiftsWithIncentives'),
          description: `${t('seeNextShiftsWithIncentives', {
            count: filteredShifts.length,
          })}`,
          shifts: filteredShifts,
          params: { filters: shiftsWithIncentivesFilters, schedule },
        }
      }
    }
  }

  function createUpcomingShiftsTile(noOfUpcomingShiftDays: number) {
    if (shifts) {
      const dates = Object.keys(shifts)
      const inputDate = moment()
      let upcomingShiftDateCounter = 0
      for (const date of dates) {
        if (moment(date).isAfter(inputDate) && shifts[date].length) {
          const filteredShifts = getOpenShiftsFromCalendarEvents(shifts, date)
          upcomingShiftDateCounter += 1
          if (upcomingShiftDateCounter === noOfUpcomingShiftDays)
            return {
              name: `${moment(date).format('dddd Do')}`,
              description: `${t('seeNextShiftsOn', {
                count: filteredShifts.length,
                date: moment(date).format('dddd Do'),
              })}
                  `,
              shifts: filteredShifts,
              params: { date },
            }
        }
      }
    }
  }
}

function getOpenShiftsFromCalendarEvents(
  events: CalendarEvents,
  date?: DateString
) {
  const event = date ? events[date] : Object.values(events).flat()
  return event.flatMap(e => (e.type === 'open_shifts' ? e.shifts : []))
}
